import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../icons';
import { StaffAudit, StaffMemberView, StaffState } from '../types';
import { Loading, ViewHeader } from '../ui';
import { AuditTab } from './AuditTab';
import { finalCaps, grantable, roleOf } from './helpers';
import { MatrixTab } from './MatrixTab';
import { MembersTab } from './MembersTab';
import { RolesTab } from './RolesTab';
import { ToolsTab } from './ToolsTab';

type Tab = 'members' | 'roles' | 'matrix' | 'audit' | 'tools';

const TABS: Array<{ id: Tab; label: string; icon: string }> = [
  { id: 'members', label: 'Membres', icon: 'users' },
  { id: 'roles', label: 'Rôles', icon: 'staff' },
  { id: 'matrix', label: 'Matrice', icon: 'grid' },
  { id: 'audit', label: 'Journal', icon: 'journal' },
  { id: 'tools', label: 'Outils', icon: 'tools' },
];

// The Staff section: owners only. Several sub-views over the same register; the server stays the authority,
// the panel applies a change locally at once ("optimistic") and replaces it with the server's state on arrival.
const StaffViewInner: React.FC<{ staff: StaffState | null; audit: StaffAudit | null }> = ({ staff, audit }) => {
  const [tab, setTab] = useState<Tab>('members');
  const [local, setLocal] = useState<Record<string, Partial<StaffMemberView>>>({});
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // The server's state is the truth again as soon as it arrives
  useEffect(() => {
    setLocal({});
  }, [staff]);

  const patchLocal = useCallback((discordId: string, patch: Partial<StaffMemberView>) => {
    setLocal((cur) => ({ ...cur, [discordId]: { ...(cur[discordId] || {}), ...patch } }));
  }, []);

  const members: StaffMemberView[] = useMemo(() => {
    if (!staff) return [];
    const order = grantable(staff).map((c) => c.id);
    return staff.members.map((m) => {
      const p = local[m.discordId];
      if (!p) return m;
      const merged = { ...m, ...p } as StaffMemberView;
      const live = merged.active && !merged.expired;
      return { ...merged, effectiveCaps: live ? finalCaps(order, roleOf(staff, merged.roleId), merged.caps, merged.denied) : [] };
    });
  }, [staff, local]);

  const onTabKey = (e: React.KeyboardEvent, i: number) => {
    let next = i;
    if (e.key === 'ArrowRight') next = (i + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') next = (i + TABS.length - 1) % TABS.length;
    else return;
    e.preventDefault();
    setTab(TABS[next].id);
    const el = tabRefs.current[TABS[next].id];
    if (el) el.focus();
  };

  if (!staff) {
    return (
      <div className="admin-panel__view">
        <ViewHeader title="Gestion du staff" />
        <Loading text="Chargement du staff" shape="cards" />
      </div>
    );
  }

  return (
    <div className="admin-panel__view admin-panel__staff">
      <ViewHeader title="Gestion du staff" subtitle="Rôles, permissions menu par menu, accès temporaires et journal des modifications." />

      <div className="admin-panel__banner" role="note">
        Les changements s'appliquent immédiatement aux personnes connectées. Seuls les propriétaires voient cette section, et le serveur revérifie chaque modification.
      </div>

      <div className="admin-panel__subtabs" role="tablist" aria-label="Sections du staff">
        {TABS.map((t, i) => (
          <button
            key={t.id}
            ref={(el) => { tabRefs.current[t.id] = el; }}
            type="button"
            role="tab"
            id={`ap-stab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls="ap-stab-panel"
            tabIndex={tab === t.id ? 0 : -1}
            className={'admin-panel__subtab' + (tab === t.id ? ' admin-panel__subtab--active' : '')}
            onClick={() => setTab(t.id)}
            onKeyDown={(e) => onTabKey(e, i)}
          >
            <Icon name={t.icon} size={15} />
            {t.label}
            {t.id === 'members' ? <span className="admin-panel__seg-count">{staff.members.length}</span> : null}
            {t.id === 'roles' ? <span className="admin-panel__seg-count">{staff.roles.length}</span> : null}
          </button>
        ))}
      </div>

      <div className="admin-panel__subpanel" role="tabpanel" id="ap-stab-panel" aria-labelledby={`ap-stab-${tab}`}>
        {tab === 'members' ? <MembersTab staff={staff} members={members} audit={audit} patchLocal={patchLocal} /> : null}
        {tab === 'roles' ? <RolesTab staff={staff} members={members} /> : null}
        {tab === 'matrix' ? <MatrixTab staff={staff} members={members} patchLocal={patchLocal} /> : null}
        {tab === 'audit' ? <AuditTab staff={staff} members={members} audit={audit} /> : null}
        {tab === 'tools' ? <ToolsTab staff={staff} /> : null}
      </div>
    </div>
  );
};

export const StaffView = React.memo(StaffViewInner);
